import axios, { AxiosInstance, AxiosResponse } from 'axios';

// Normalize and validate the API base URL to avoid Chrome-specific failures when protocol is omitted
function normalizeBaseUrl(raw?: string): string {
  let val = (raw && raw.trim()) || '';
  // Empty value means use relative paths (for production with nginx proxy)
  if (!val) return '';

  // If the value lacks a protocol, prepend http:// (Chrome is stricter than Safari/Brave here)
  if (!/^https?:\/\//i.test(val)) {
    console.warn('[ApiService] REACT_APP_API_URL is missing protocol. Prefixing with http:// =>', val);
    val = 'http://' + val; // Could be upgraded to https later if backend supports it
  }

  // Remove trailing slash
  val = val.replace(/\/$/, '');

  // Safety guard: in production over HTTPS, avoid hard-coded ports (e.g., :3001) or http protocol for same host.
  try {
    // Only apply in browser contexts
    const isBrowser = typeof window !== 'undefined';
    if (isBrowser && process.env.NODE_ENV === 'production' && window.location.protocol === 'https:') {
      const url = new URL(val);
      const sameHost = url.hostname === window.location.hostname;

      // If pointing to the same host but with a non-standard port (e.g., 3001), prefer same-origin (empty base URL)
      // Also, if protocol is http for the same host while page is https, prefer same-origin.
      if (
        sameHost && (
          (url.port && url.port !== '' && url.port !== '443') || url.protocol === 'http:'
        )
      ) {
        console.warn('[ApiService] Overriding REACT_APP_API_URL to same-origin due to HTTPS production context:', val);
        return '';
      }
    }
  } catch (e) {
    // If URL parsing fails, fall back to returning the value as-is
    console.warn('[ApiService] Failed to parse API base URL, using as-is:', val, e);
  }

  return val;
}

const API_BASE_URL = normalizeBaseUrl(process.env.REACT_APP_API_URL);
console.log('[ApiService] Using API Base URL (normalized):', API_BASE_URL);

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // Increase timeout to 30 seconds
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        console.log('[ApiService] Interceptor: Full request config:', {
          url: config.url,
          baseURL: config.baseURL,
          method: config.method,
          fullURL: `${config.baseURL}${config.url}`,
          params: config.params,
          signal: config.signal
        });
        console.log('[ApiService] Interceptor: Token from localStorage:', token);
        
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          console.log('[ApiService] Interceptor: Authorization header set:', `Bearer ${token.substring(0, 10)}...`);
        } else {
          console.log('[ApiService] Interceptor: No token in localStorage, Authorization header not set.');
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          console.log('[ApiService] Interceptor: 401 Unauthorized response received, clearing auth data');
          localStorage.removeItem('token');
          localStorage.removeItem('user');

          // Don't redirect if we're on public pages (login, tablet)
          const publicPages = ['/login', '/tablet', '/tablet-sign-in'];
          const isPublicPage = publicPages.some(page => window.location.pathname.includes(page));

          if (!isPublicPage) {
            console.log('[ApiService] Interceptor: Redirecting to login page');
            window.location.href = '/login';
          } else {
            console.log('[ApiService] Interceptor: On public page, not redirecting');
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth endpoints
  async login(credentials: { username: string; password: string }) {
    console.log('[ApiService] login: Making login request with credentials:', { 
      username: credentials.username, 
      passwordProvided: !!credentials.password 
    });
    try {
      // Set explicit headers for login request
      const response = await this.api.post('/api/auth/login', credentials, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      console.log('[ApiService] login: Received response from server:', {
        status: response.status,
        statusText: response.statusText,
        hasToken: !!response.data.token,
        hasUser: !!response.data.user,
        message: response.data.message
      });
      
      if (response.data.token) {
        console.log('[ApiService] login: Token received, first 15 chars:', response.data.token.substring(0, 15));
      } else {
        console.error('[ApiService] login: No token in response!');
      }
      
      return response.data;
    } catch (error: any) {
      console.error('[ApiService] login: Error during login:', {
        message: error.message,
        response: error.response ? {
          status: error.response.status,
          data: error.response.data
        } : 'No response'
      });
      throw error;
    }
  }

  async register(userData: { 
    username: string; 
    password: string; 
    email: string; 
    firstName?: string; 
    lastName?: string; 
  }) {
    const response = await this.api.post('/api/auth/register', userData);
    return response.data;
  }

  async logout() {
    const response = await this.api.post('/api/auth/logout');
    return response.data;
  }

  async verifyToken(signal?: AbortSignal) {
    console.log('[ApiService] verifyToken: Attempting to verify token');
    try {
      // Check localStorage directly before making the request
      const localToken = localStorage.getItem('token');
      console.log('[ApiService] verifyToken: Token in localStorage:', !!localToken);
      
      // Log request details
      console.log('[ApiService] verifyToken: Sending request to /api/auth/me');
      const response = await this.api.get('/api/auth/me', { signal });
      
      console.log('[ApiService] verifyToken: Request successful');
      console.log('[ApiService] verifyToken: Response data:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('[ApiService] verifyToken: Error verifying token:', {
        message: error.message,
        response: error.response ? {
          status: error.response.status,
          data: error.response.data
        } : 'No response'
      });
      throw error;
    }
  }

  // Member endpoints
  async getMembers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    type?: string;
    active?: boolean | string;
    tags?: string;
    paymentStatus?: string;
    startDateFrom?: string;
    startDateTo?: string;
    endDateFrom?: string;
    endDateTo?: string;
  }, signal?: AbortSignal) {
    console.log('[ApiService] getMembers: Starting API request with params:', params);
    try {
      // Check if signal is already aborted before making the request
      if (signal?.aborted) {
        console.log('[ApiService] getMembers: Request was aborted before it was sent');
        throw new Error('Request aborted');
      }
      
      const response = await this.api.get('/api/members', { 
        params,
        signal 
      });
      console.log('[ApiService] getMembers: Response received:', response.data);
      return response.data;
    } catch (error: any) {
      // Don't log aborted requests as errors
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('[ApiService] getMembers: Request was canceled or aborted');
      } else {
        console.error('[ApiService] getMembers: Error during API call:', error);
      }
      throw error;
    }
  }

  async getDeletedMembers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    type?: string;
  }, signal?: AbortSignal) {
    console.log('[ApiService] getDeletedMembers: Starting API request with params:', params);
    try {
      if (signal?.aborted) {
        console.log('[ApiService] getDeletedMembers: Request was aborted before it was sent');
        throw new Error('Request aborted');
      }

      const response = await this.api.get('/api/members/deleted', {
        params,
        signal
      });
      console.log('[ApiService] getDeletedMembers: Response received:', response.data);
      return response.data;
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('[ApiService] getDeletedMembers: Request was canceled or aborted');
      } else {
        console.error('[ApiService] getDeletedMembers: Error during API call:', error);
      }
      throw error;
    }
  }

  async getMember(id: string, signal?: AbortSignal) {
    const response = await this.api.get(`/api/members/${id}`, { signal });
    return response.data;
  }

  // Public member search for tablet (no authentication required)
  async searchMembersPublic(params: {
    search: string;
    limit?: number;
  }, signal?: AbortSignal) {
    console.log('[ApiService] searchMembersPublic: Starting public search with params:', params);
    try {
      if (signal?.aborted) {
        console.log('[ApiService] searchMembersPublic: Request was aborted before it was sent');
        throw new Error('Request aborted');
      }

      const response = await this.api.get('/api/checkins/publicsearch', {
        params,
        signal
      });
      console.log('[ApiService] searchMembersPublic: Response received:', response.data);
      return response.data;
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('[ApiService] searchMembersPublic: Request was canceled or aborted');
      } else {
        console.error('[ApiService] searchMembersPublic: Error during API call:', error);
      }
      throw error;
    }
  }

  async getAllMembers(signal?: AbortSignal) {
    const response = await this.api.get(`/api/members`, { signal });
    return response.data;
  }
  

  async createMember(memberData: any) {
    const response = await this.api.post('/api/members', memberData);
    return response.data;
  }

  async updateMember(id: string, memberData: any) {
    console.log('[ApiService] updateMember: Updating member ID:', id);
    console.log('[ApiService] updateMember: Request URL will be:', `/api/members/${id}`);
    console.log('[ApiService] updateMember: Member data:', memberData);
    const response = await this.api.put(`/api/members/${id}`, memberData);
    return response.data;
  }

  async deleteMember(id: string) {
    const response = await this.api.delete(`/api/members/${id}`);
    return response.data;
  }

  async restoreMember(id: string) {
    const response = await this.api.post(`/api/members/${id}/restore`);
    return response.data;
  }

  // Member stats endpoint
  async getMemberStats() {
    const response = await this.api.get('/api/members/stats/overview');
    return response.data;
  }

  // Upload member photo
  async uploadMemberPhoto(id: string, file: File) {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await this.api.post(`/api/members/${id}/photo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Request photo from tablet
  async requestMemberPhoto(id: string) {
    const response = await this.api.post(`/api/members/${id}/request-photo`);
    return response.data;
  }

  // Delete member photo
  async deleteMemberPhoto(id: string) {
    const response = await this.api.delete(`/api/members/${id}/photo`);
    return response.data;
  }

  // Email endpoints
  async sendEmail(emailData: {
    subject: string;
    body: string;
    recipients: 'all' | 'selected' | 'single';
    selectedMembers?: string[];
    singleEmail?: string;
    templateId?: string;
    variables?: Record<string, string>;
  }) {
    const response = await this.api.post('/api/emails/send', emailData);
    return response.data;
  }

  async getEmailTemplates() {
    const response = await this.api.get('/api/emails/templates');
    return response.data;
  }

  async saveEmailTemplate(template: any) {
    const response = await this.api.post('/api/emails/templates', template);
    return response.data;
  }

  async getEmailTemplate(id: string) {
    const response = await this.api.get(`/api/emails/templates/${id}`);
    return response.data;
  }

  async updateEmailTemplate(id: string, template: any) {
    const response = await this.api.put(`/api/emails/templates/${id}`, template);
    return response.data;
  }

  async deleteEmailTemplate(id: string) {
    const response = await this.api.delete(`/api/emails/templates/${id}`);
    return response.data;
  }

  async getEmailVariables() {
    const response = await this.api.get('/api/emails/variables');
    return response.data;
  }

  async sendTestEmail(email: string) {
    const response = await this.api.post('/api/emails/test', { email });
    return response.data;
  }

  async getEmailConfig() {
    const response = await this.api.get('/api/emails/config');
    return response.data;
  }

  async testEmailConfig() {
    const response = await this.api.post('/api/emails/config/verify');
    return response.data;
  }

  // User settings endpoints
  async getUserSettings() {
    const response = await this.api.get('/api/users/settings');
    return response.data;
  }

  async saveUserSettings(settings: any) {
    const response = await this.api.put('/api/users/settings', settings);
    return response.data;
  }

  // Admin endpoints
  async createUser(userData: any) {
    const response = await this.api.post('/api/admin/users', userData);
    return response.data;
  }

  async getAllUsers() {
    const response = await this.api.get('/api/admin/users');
    return response.data;
  }
  
  // Check-in endpoints
  async checkInMember(checkInData: {
    memberId?: string;
    idNumber?: string;
    deviceId: string;
    deviceType: 'tablet' | 'kiosk' | 'admin';
    notes?: string;
  }) {
    const response = await this.api.post('/api/checkins', checkInData);
    return response.data;
  }
  
  async getMemberCheckIns(memberId: string, params?: {
    page?: number;
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await this.api.get(`/api/checkins/member/${memberId}${queryString}`);
    return response.data;
  }

  // Family member lookup for estate-based sign-ins
  async lookupFamilyByAddress(address: string) {
    const response = await this.api.get(`/api/checkins/family-lookup/${encodeURIComponent(address)}`);
    return response.data;
  }

  // Family member check-in
  async checkInFamilyMember(checkInData: {
    propertyId: string;
    familyMemberName: string;
    deviceId: string;
    deviceType: 'tablet' | 'kiosk' | 'admin';
    notes?: string;
  }) {
    const response = await this.api.post('/api/checkins/family-checkin', checkInData);
    return response.data;
  }
  
  async getRecentCheckIns(limit: number = 10) {
    const response = await this.api.get(`/api/checkins/recent?limit=${limit}`);
    return response.data;
  }
  
  async getCheckInStats() {
    const response = await this.api.get('/api/checkins/stats');
    return response.data;
  }

  async getTodaysCheckIns() {
    const response = await this.api.get('/api/checkins/today');
    return response.data;
  }

  async getCheckIns(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    deviceType?: string;
    startDate?: string;
    endDate?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.deviceType) queryParams.append('deviceType', params.deviceType);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await this.api.get(`/api/checkins${queryString}`);
    return response.data;
  }

  // Payment endpoints
  async createPayment(paymentData: any) {
    const response = await this.api.post('/api/payments', paymentData);
    return response.data;
  }

  async getPayments(params?: {
    page?: number;
    limit?: number;
    search?: string;
    paymentMethod?: string;
    paymentType?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    memberId?: string;
    processedBy?: string;
  }) {
    const response = await this.api.get('/api/payments', { params });
    return response.data;
  }

  async getPayment(id: string) {
    const response = await this.api.get(`/api/payments/${id}`);
    return response.data;
  }

  async getMemberPayments(memberId: string, params?: {
    page?: number;
    limit?: number;
  }) {
    const response = await this.api.get(`/api/payments/member/${memberId}`, { params });
    return response.data;
  }

  async getPaymentStats() {
    const response = await this.api.get('/api/payments/stats');
    return response.data;
  }

  async refundPayment(paymentId: string, refundData: {
    amount: number;
    reason: string;
    notes?: string;
  }) {
    const response = await this.api.post(`/api/payments/${paymentId}/refund`, refundData);
    return response.data;
  }

  async getMembershipPlans() {
    const response = await this.api.get('/api/membership-plans');
    return response.data;
  }

  // Audit Log endpoints
  async getAuditLogs(params: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    userId?: string;
    action?: string;
    resourceType?: string;
    status?: string;
  } = {}) {
    const response = await this.api.get('/api/audit', { params });
    return response.data;
  }

  async getAuditLogStats(days: number = 30) {
    const response = await this.api.get('/api/audit/stats', { params: { days } });
    return response.data;
  }

  async getUserActivity(userId: string, params: {
    limit?: number;
    skip?: number;
    startDate?: string;
    endDate?: string;
    actions?: string[];
    resourceTypes?: string[];
  } = {}) {
    const queryParams = {
      ...params,
      actions: params.actions?.join(','),
      resourceTypes: params.resourceTypes?.join(',')
    };
    const response = await this.api.get(`/api/audit/user/${userId}`, { params: queryParams });
    return response.data;
  }

  async getAuditLogById(id: string) {
    const response = await this.api.get(`/api/audit/${id}`);
    return response.data;
  }

  async exportAuditLogs(params: {
    startDate?: string;
    endDate?: string;
  } = {}) {
    const response = await this.api.get('/api/audit/export/csv', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  }

  async cleanupAuditLogs(days: number = 365) {
    const response = await this.api.delete('/api/audit/cleanup', { params: { days } });
    return response.data;
  }

  // Daily Check-in History endpoints
  async getDailyHistory(params: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    sortBy?: string;
    sortOrder?: string;
  } = {}) {
    const response = await this.api.get('/api/daily-history', { params });
    return response.data;
  }

  async getDailyTrends(days: number = 30) {
    const response = await this.api.get('/api/daily-history/trends', { params: { days } });
    return response.data;
  }

  async getDailyRecord(date: string) {
    const response = await this.api.get(`/api/daily-history/day/${date}`);
    return response.data;
  }

  async getTodayStats() {
    const response = await this.api.get('/api/daily-history/today');
    return response.data;
  }

  async updateDailyStats(date: string) {
    const response = await this.api.post(`/api/daily-history/update/${date}`);
    return response.data;
  }

  async bulkUpdateDailyHistory(startDate: string, endDate: string) {
    const response = await this.api.post('/api/daily-history/bulk-update', {
      startDate,
      endDate
    });
    return response.data;
  }

  async getPeakHours(days: number = 30) {
    const response = await this.api.get('/api/daily-history/peak-hours', { params: { days } });
    return response.data;
  }

  async exportDailyHistory(params: {
    startDate?: string;
    endDate?: string;
  } = {}) {
    const response = await this.api.get('/api/daily-history/export/csv', {
      params,
      responseType: 'blob'
    });
    return response.data;
  }

  // Estate Address endpoints
  async getAvailableEstateAddresses() {
    const response = await this.api.get('/api/members/estate-addresses/available');
    return response.data;
  }

  async getEstateAddressesByVillage(villageName: string, includeOccupied: boolean = false) {
    const response = await this.api.get(`/api/members/estate-addresses/village/${encodeURIComponent(villageName)}`, {
      params: { includeOccupied }
    });
    return response.data;
  }

  async getEstateAddressOverview(params?: {
    page?: number;
    limit?: number;
    search?: string;
    occupied?: 'true' | 'false' | 'all';
    village?: string;
  }, signal?: AbortSignal) {
    console.log('[ApiService] getEstateAddressOverview: Starting API request with params:', params);
    try {
      if (signal?.aborted) {
        console.log('[ApiService] getEstateAddressOverview: Request was aborted before it was sent');
        throw new Error('Request aborted');
      }

      const response = await this.api.get('/api/estate-addresses/overview', {
        params,
        signal
      });
      console.log('[ApiService] getEstateAddressOverview: Response received:', response.data);
      return response.data;
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        console.log('[ApiService] getEstateAddressOverview: Request was canceled or aborted');
      } else {
        console.error('[ApiService] getEstateAddressOverview: Error during API call:', error);
      }
      throw error;
    }
  }

  // Generic methods
  async get<T = any>(url: string, params?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.api.get(url, { params });
    return response.data;
  }

  async post<T = any>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.api.post(url, data);
    return response.data;
  }

  async put<T = any>(url: string, data?: any): Promise<T> {
    const response: AxiosResponse<T> = await this.api.put(url, data);
    return response.data;
  }

  async delete<T = any>(url: string): Promise<T> {
    const response: AxiosResponse<T> = await this.api.delete(url);
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;
